/**
 * React component for the image section on the Learn Home page.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - Data containing information for the Learn Home image section.
 * @returns {JSX.Element} React component.
 */

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import SpillDetectionAnimation from "./SpillDetectionAnimation";
import LazyVideoNew from "../LazyVideo/LazyVideoNew";
import { motion, useInView, useAnimation } from "framer-motion";

const transitionLine = { duration: 10, ease: "easeInOut" };

export default function LearnHomeImageSection({ data }) {
  const ref1 = useRef();
  const ref2 = useRef();
  const robot1TrackInView = useInView(ref1, { once: true, amount: 0.3 });
  const robot2TrackInView = useInView(ref2, { once: true, amount: 0.3 });

  const controls = useAnimation();
  const pathControls = useAnimation();

  const controls2 = useAnimation();
  const pathControls2 = useAnimation();

  useEffect(() => {
    if (robot1TrackInView) {
      controls.start({ offsetDistance: "100%" });
      pathControls.start({ pathLength: 1 });
    }
    return () => {
      controls.stop();
      pathControls.stop();
    };
  }, [controls, robot1TrackInView]);

  // Commenting track animation between Key feature 2 & 3
  // useEffect(() => {
  //   if (robot2TrackInView) {
  //     controls2.start({ offsetDistance: "100%" });
  //     pathControls2.start({ pathLength: 1 });
  //   }
  //   return () => {
  //     controls2.stop();
  //     pathControls2.stop();
  //   };
  // }, [controls2, robot2TrackInView]);
  return (
    <>
      <div className="lg:grid grid-cols-18 hidden pt-[38px] lg:pt-[60px]">
        <div className="col-span-18 mr-[80px] ml-[40px]">
          <div className="w-[100%] relative">
            <div className="aspect-w-1320 aspect-h-1561">
              <div className="h-full w-full">
                <div className="h-full relative">
                  <div
                    className={`w-[42.42%] absolute left-[3.1%] top-[2.6%] bg-white`}
                  >
                      <div className="aspect-w-560 aspect-h-560">
                        <LazyVideoNew
                          videoImagePath={data.learnsNonstopVideoContainer1}
                          isMobile={false}
                          customClasses={`h-full w-full object-cover object-center rounded-[16px]`}
                        />
                      </div>
                  </div>

                  <div className={`absolute left-0`}>
                      <div className="w-[240px] h-[160px] flex justify-center items-center bg-lavender rounded-[16px]">
                        <h6
                          className={`md:flex hidden text-center py-[46px] px-[32px] text-primary`}
                        >
                          Continual photorealistic 3D mapping
                        </h6>
                      </div>
                  </div>
                  <div
                    className={`w-[61%] xl:w-[47%] bigger:w-[42%] absolute right-[12.6%] top-[10.5%] -z-[1] bg-white`}
                    ref={ref1}
                  >
                    {/* <div className="aspect-w-72 aspect-h-77">
                      <Image
                        priority
                        src={`https:${data?.trackConnector1?.fields?.file?.url}`}
                        layout="fill"
                        alt="Connector 1"
                        className="custom-img"
                      />
                    </div> */}
                    <div className="aspect-w-332 aspect-h-252 relative">
                      <svg
                        width="546"
                        height="505"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <motion.path
                          d="M0 3H412C484.35 3 543 61.6507 543 134V505"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={pathControls}
                          transition={transitionLine}
                          strokeWidth="6"
                        />
                        <motion.path
                          d="M0.000488281 39.5H404.5C459.729 39.5 504.5 84.2715 504.5 139.5V505"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={pathControls}
                          transition={transitionLine}
                          strokeWidth="6"
                        />
                        <motion.path
                          d="M0 77H402.501C437.295 77 465.501 105.206 465.501 140V505"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={pathControls}
                          transition={transitionLine}
                          strokeWidth="6"
                        />
                      </svg>
                      <motion.div
                        className={`maticOnRightSideTrackRobot w-[182px] h-[136px] rotate-0`}
                        initial={{ offsetDistance: "0%" }}
                        animate={controls}
                        transition={transitionLine}
                      >
                        <Image
                          src={`https:${data?.maticMovingOnTrackRobot?.fields?.file?.url}`}
                          className="w-[182px] h-[136px]"
                          alt="Moving Robot"
                          width="182"
                          height="136"
                        />
                      </motion.div>
                    </div>
                  </div>
                  <div
                    className={`w-[36.36%] absolute right-0 top-[23.1%] bg-white`}
                  >
                      <div className="aspect-w-480 aspect-h-480 relative">
                        <LazyVideoNew
                          videoImagePath={data.learnsNonstopVideoContainer2}
                          isMobile={false}
                          customClasses={`h-full w-full object-cover object-center rounded-[16px]`}
                        />
                      </div>
                  </div>

                  <div className={`absolute top-[20.5%] left-[60.3%]`}>
                      <div className="w-[240px] h-[160px] flex justify-center items-center bg-lavender rounded-[16px]">
                        <h6
                          className={`md:flex hidden text-center py-[46px] px-[32px]`}
                        >
                          Zero stuck. Maximum clean.
                        </h6>
                      </div>
                  </div>

                  <div
                    className={`w-[35.06%] absolute left-[33.6%] top-[46%] -z-[1] bg-white`}
                    ref={ref2}
                  >
                    <div className="aspect-w-508 aspect-h-521 relative">
                      <svg
                        width="508"
                        height="521"
                        viewBox="0 0 508 521"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M507.5 76.125L138.879 76.125C104.315 76.125 76.1875 104.227 76.1875 138.761L76.1875 520.699"
                          stroke="#ECEAE3"
                          strokeWidth="6"
                          strokeMiterlimit="10"
                        />
                        <path
                          d="M3.00002 516.699L3 130.746C3 60.3117 60.363 3 130.877 3L500.5 3.00002"
                          stroke="#ECEAE3"
                          strokeWidth="6"
                          strokeMiterlimit="10"
                        />
                        <path
                          d="M505.5 39.5625L134.878 39.5625C82.2575 39.5625 39.5937 82.1881 39.5937 134.762L39.5938 519.699"
                          stroke="#ECEAE3"
                          strokeWidth="6"
                          strokeMiterlimit="10"
                        />
                      </svg>
                      {/* <svg
                        width="522"
                        height="564"
                        viewBox="0 0 522 564"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <motion.path
                          d="M621 3H131C60.3076 3 3 60.3076 3 131V563"
                          fill="transparent"
                          strokeWidth="6"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 1 }}
                          animate={pathControls2}
                          transition={transitionLine}
                        />
                        <motion.path
                          d="M621 40H136C82.9807 40 40 82.9807 40 136V563"
                          fill="transparent"
                          strokeWidth="6"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 1 }}
                          animate={pathControls2}
                          transition={transitionLine}
                        />
                        <motion.path
                          d="M621 76H140C105.206 76 77 104.206 77 139V563"
                          fill="transparent"
                          strokeWidth="6"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 1 }}
                          animate={pathControls2}
                          transition={transitionLine}
                        />
                      </svg>
                      <motion.div
                        className={`maticRobotContainer w-[182px] h-[136px]`}
                        initial={{ offsetDistance: "0%" }}
                        animate={controls2}
                        transition={transitionLine}
                      >
                        <Image
                          src={`https:${data?.maticMovingOnTrackRobot?.fields?.file?.url}`}
                          className="w-[182px] h-[136px] hidden"
                          alt="Moving Robot"
                          width="182"
                          height="136"
                        />
                      </motion.div> */}
                    </div>
                  </div>

                  <div
                    className={`w-[60.60%] absolute bottom-0 left-[21.3%] bg-white rounded-[16px]`}
                  >
                      <div className="aspect-w-800 aspect-h-560 relative">
                        <LazyVideoNew
                          videoImagePath={data.learnsNonstopVideoContainer3}
                          isMobile={false}
                          customClasses={`h-full w-full object-cover object-center rounded-[16px]`}
                        />
                      </div>
                  </div>

                  <div className={`absolute top-[61.2%] left-[18.2%]`}>
                      <div className="w-[240px] h-[160px] flex justify-center items-center bg-lavender rounded-[16px]">
                        <h6
                          className={`md:flex hidden text-center py-[46px] px-[32px]`}
                        >
                          {data?.quietEnoughForWorkTimeText}
                        </h6>
                      </div>
                  </div>

                  <SpillDetectionAnimation data={data} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* flex for mobile*/}
      <div
        className={`flex flex-col lg:hidden mt-[40px] h-full justify-center items-center`}
      >
        <div className="lg:hidden block mb-[80px] relative w-full max-w-[560px]">
            <div className="aspect-w-327 aspect-h-327">
              <LazyVideoNew
                videoImagePath={data.learnsNonstopVideoContainer1}
                isMobile={true}
                customClasses={`h-full w-full object-cover object-center rounded-8`}
              />
            </div>
            <div
              className={`bg-lavender rounded-[8px] w-[calc(100%-80px)] m-auto absolute -bottom-[4%] mob:-bottom-[11%] left-[40px]`}
            >
              <h6
                className={`flex text-center justify-center items-center px-[30px] py-[12px]`}
              >
                Continual photorealistic 3D mapping
              </h6>
            </div>
        </div>

        <div className="lg:hidden block mb-[80px] relative w-full max-w-[560px]">
            <div className="aspect-w-327 aspect-h-327">
              <LazyVideoNew
                videoImagePath={data.learnsNonstopVideoContainer2}
                isMobile={true}
                customClasses={`h-full w-full object-cover object-center rounded-8`}
              />
            </div>
            <div
              className={`bg-lavender rounded-[8px] w-[calc(100%-80px)] m-auto absolute -bottom-[4%] mob:-bottom-[11%] left-[40px]`}
            >
              <h6
                className={`flex text-center justify-center items-center px-[35px] py-[12px]`}
              >
                Zero stuck. Maximum clean.
              </h6>
            </div>
        </div>
        <div className="lg:hidden block mb-[80px] relative w-full max-w-[560px]">
            <div className="aspect-w-327 aspect-h-327">
              <LazyVideoNew
                videoImagePath={data.learnsNonstopVideoContainer3}
                isMobile={true}
                customClasses={`h-full w-full object-cover object-center rounded-8`}
              />
            </div>
            <div
              className={`bg-lavender rounded-[8px] w-[calc(100%-80px)] m-auto absolute -bottom-[4%] mob:-bottom-[11%] left-[40px]`}
            >
              <h6
                className={`flex text-center justify-center items-center px-[30px] py-[12px]`}
              >
                {data?.quietEnoughForWorkTimeText}
              </h6>
            </div>
        </div>
      </div>
    </>
  );
}
